import { z } from "zod";

import { bookingBankTransferSchema } from "./bank-transfer.schema";

// How a traveller chose to pay at checkout. `online` is the payment gateway
// (Paylexer). `pay_later` (Book Now, Pay Later) and `bank_transfer` take no
// money at checkout; the booking stays pending until the agency is paid in
// full. A `bank_transfer` booking holds its seats until its transfer-by date;
// a `pay_later` booking holds none.
export const checkoutPaymentMethodSchema = z.enum([
  "online",
  "pay_later",
  "bank_transfer",
]);
export type CheckoutPaymentMethod = z.infer<typeof checkoutPaymentMethodSchema>;

export const OFFLINE_PAYMENT_METHODS = [
  "pay_later",
  "bank_transfer",
] as const satisfies readonly CheckoutPaymentMethod[];

// The ids the checkout-order list uses for our own methods. Every other entry
// in `checkoutMethodOrder` is a Paylexer gateway code, forwarded to its widget.
export const NATIVE_CHECKOUT_METHOD_IDS = OFFLINE_PAYMENT_METHODS;

// Ids an earlier, unsaved settings mock-up used for the same two rows.
export const LEGACY_NATIVE_METHOD_IDS: Readonly<Record<string, string>> = {
  "reserve-now-pay-later": "pay_later",
  "bank-deposit": "bank_transfer",
};

export const payLaterDeadlinePolicySchema = z.enum(["cancel", "remind"]);
export type PayLaterDeadlinePolicy = z.infer<
  typeof payLaterDeadlinePolicySchema
>;

const methodIdSchema = z.string().trim().min(1).max(64);

export const paymentMethodSettingsShape = {
  /** Every method id in the order travellers see it: gateway codes and
   *  native ids mixed. An id not listed falls to the end. */
  checkoutMethodOrder: z.array(methodIdSchema).max(50),
  /** Gateway codes the agency turned off. Native methods use their own
   *  `…Enabled` flag, so a new agency starts with them off. */
  hiddenCheckoutMethods: z.array(methodIdSchema).max(50),
  /** Pre-selected at checkout. Null = the first method that is on. */
  defaultPaymentMethod: methodIdSchema.nullable(),
  payLaterEnabled: z.boolean(),
  bankTransferEnabled: z.boolean(),
  /** Most unpaid offline bookings one traveller (by email) may hold for the
   *  same tour date. */
  unpaidBookingLimit: z
    .number()
    .int()
    .min(1, "Allow at least 1 unpaid booking.")
    .max(10, "Allow at most 10 unpaid bookings."),
  /** Days before the tour date by which a Book Now, Pay Later booking must
   *  be paid in full. */
  payLaterDeadlineDays: z
    .number()
    .int()
    .min(0, "Enter 0 days or more.")
    .max(365, "Enter 365 days or fewer."),
  payLaterDeadlinePolicy: payLaterDeadlinePolicySchema,
  /** One free-text block (bank name, account name, number, …) shown to the
   *  traveller exactly as typed. Required while Bank Transfer is on. Only
   *  booking-scoped replies carry it — never `storefrontPaymentMethodsSchema`.
   *  Changing it needs a recent identity check and writes a history row. */
  bankTransferDetails: z
    .string()
    .max(2000, "Keep the bank details to 2000 characters or fewer.")
    .nullable(),
  /** Days after the booking date the traveller has to send the transfer. The
   *  transfer-by date is never later than the tour date. */
  bankTransferDays: z
    .number()
    .int()
    .min(1, "Enter 1 day or more.")
    .max(60, "Enter 60 days or fewer."),
  /** Shown under Bank Transfer in the checkout payment list. */
  bankTransferMessage: z
    .string()
    .max(240, "Keep the message to 240 characters or fewer.")
    .nullable(),
};

export type PaymentMethodSettingsDto = z.infer<
  z.ZodObject<typeof paymentMethodSettingsShape>
>;

export const PAYMENT_METHOD_SETTINGS_DEFAULTS: PaymentMethodSettingsDto = {
  checkoutMethodOrder: [],
  hiddenCheckoutMethods: [],
  defaultPaymentMethod: null,
  payLaterEnabled: false,
  bankTransferEnabled: false,
  unpaidBookingLimit: 1,
  payLaterDeadlineDays: 7,
  payLaterDeadlinePolicy: "remind",
  bankTransferDetails: null,
  bankTransferDays: 3,
  bankTransferMessage: null,
};

function isNativeMethodOn(
  value: PaymentMethodSettingsDto,
  id: string,
): boolean | undefined {
  if (id === "pay_later") return value.payLaterEnabled;
  if (id === "bank_transfer") return value.bankTransferEnabled;
  return undefined;
}

/** Cross-field rules, called from `checkoutSettingsSchema`'s superRefine. */
export function refinePaymentMethodSettings(
  value: PaymentMethodSettingsDto,
  ctx: z.RefinementCtx,
): void {
  if (value.bankTransferEnabled && !value.bankTransferDetails?.trim()) {
    ctx.addIssue({
      code: "custom",
      path: ["bankTransferDetails"],
      message: "Enter your bank details to turn on Bank Transfer.",
    });
  }
  const id = value.defaultPaymentMethod;
  if (id === null) return;
  const nativeOn = isNativeMethodOn(value, id);
  const off =
    nativeOn === undefined
      ? value.hiddenCheckoutMethods.includes(id)
      : !nativeOn;
  if (off) {
    ctx.addIssue({
      code: "custom",
      path: ["defaultPaymentMethod"],
      message: "Choose a payment method that is turned on.",
    });
  }
}

/** Per-field repairs for a stored row that no longer passes the rules above.
 *  Merged into `checkoutSettings`' NEUTRALISE map. */
export const PAYMENT_METHOD_NEUTRALISE: Record<
  string,
  (draft: Record<string, unknown>) => void
> = {
  defaultPaymentMethod: (d) => {
    d["defaultPaymentMethod"] = null;
  },
  bankTransferEnabled: (d) => {
    d["bankTransferEnabled"] = false;
  },
  bankTransferDetails: (d) => {
    d["bankTransferDetails"] =
      PAYMENT_METHOD_SETTINGS_DEFAULTS.bankTransferDetails;
    d["bankTransferEnabled"] = false;
  },
  bankTransferDays: (d) => {
    d["bankTransferDays"] = PAYMENT_METHOD_SETTINGS_DEFAULTS.bankTransferDays;
  },
  bankTransferMessage: (d) => {
    d["bankTransferMessage"] =
      PAYMENT_METHOD_SETTINGS_DEFAULTS.bankTransferMessage;
  },
  unpaidBookingLimit: (d) => {
    d["unpaidBookingLimit"] =
      PAYMENT_METHOD_SETTINGS_DEFAULTS.unpaidBookingLimit;
  },
  payLaterDeadlineDays: (d) => {
    d["payLaterDeadlineDays"] =
      PAYMENT_METHOD_SETTINGS_DEFAULTS.payLaterDeadlineDays;
  },
};

/** The checkout methods a storefront shows, in order, with the default marked.
 *  Gateway codes are listed as the agency ordered them; whether Paylexer offers
 *  each one is decided by its widget, as today. */
export const storefrontPaymentMethodsSchema = z.object({
  order: z.array(z.string()),
  hidden: z.array(z.string()),
  defaultMethod: z.string().nullable(),
  onlineAvailable: z.boolean(),
  payLaterAvailable: z.boolean(),
  bankTransferAvailable: z.boolean(),
  /** The operator's checkout message under Bank Transfer. Never the bank
   *  details: this reply is public. */
  bankTransferMessage: z.string().nullable(),
  /** Online payments go to the agency's Paylexer test keys, so no real money
   *  moves. False whenever `onlineAvailable` is false. */
  testMode: z.boolean(),
});
export type StorefrontPaymentMethodsDto = z.infer<
  typeof storefrontPaymentMethodsSchema
>;

/** `GET /checkout/payment-methods?date=` — the tour date the traveller is
 *  booking. When its Pay Later deadline has already passed, the answer says
 *  `payLaterAvailable: false`. Bank Transfer stays available up to the tour
 *  date itself, because its transfer-by date is capped there. */
export const storefrontPaymentMethodsQuerySchema = z.object({
  date: z.string().date().optional(),
});
export type StorefrontPaymentMethodsQueryDto = z.infer<
  typeof storefrontPaymentMethodsQuerySchema
>;

/** Where a booking's payment stands, as every screen labels it. */
export const bookingPaymentLabelSchema = z.enum([
  "pay_later",
  "awaiting_transfer",
  "transfer_reported",
  "transfer_not_received",
  "partly_paid",
  "paid_in_full",
  "unpaid",
]);
export type BookingPaymentLabel = z.infer<typeof bookingPaymentLabelSchema>;

/** The offline-payment facts a booking reports to the traveller and to staff. */
export const bookingOfflinePaymentSchema = z.object({
  paymentMethod: checkoutPaymentMethodSchema,
  paymentLabel: bookingPaymentLabelSchema,
  /** The last day to pay, in the agency's timezone. Fixed when the booking was
   *  placed; a later settings change does not move it. For Bank Transfer this
   *  is the transfer-by date. */
  paymentDeadline: z.string().date().nullable(),
  /** True while the booking holds its spots: once confirmed, and for a Bank
   *  Transfer booking also while its transfer-by hold lasts. */
  spotReserved: z.boolean(),
  totalMinor: z.number().int().nonnegative(),
  paidMinor: z.number().int().nonnegative(),
  owedMinor: z.number().int().nonnegative(),
  /** Present only when `paymentMethod` is `bank_transfer`. Optional on the
   *  wire so a Pay Later reply is unchanged. */
  bankTransfer: bookingBankTransferSchema.optional(),
});
export type BookingOfflinePaymentDto = z.infer<
  typeof bookingOfflinePaymentSchema
>;

/** The traveller's own booking adds whether they may pay online now. */
export const travelerOfflinePaymentSchema = bookingOfflinePaymentSchema.extend({
  canPayOnline: z.boolean(),
});
export type TravelerOfflinePaymentDto = z.infer<
  typeof travelerOfflinePaymentSchema
>;

/** Who asked traveler-api to act on a booking, for its activity log. */
export const internalBookingActorSchema = z
  .object({
    actorType: z.enum(["admin", "system"]),
    actorId: z.string().min(1).max(200).nullable(),
    actorName: z.string().min(1).max(200),
  })
  .strict();

/** operator-api → traveler-api `POST internal/v1/bookings/:bookingId/offline-settle`,
 *  sent after staff record a succeeded payment on an offline booking, or when
 *  staff confirm one by hand. Reads the ledger itself; idempotent.
 *
 *  `confirmUnpaid` confirms even when money is still owed (a staff status
 *  change). `overbook` takes the seats even when the date is full, marking the
 *  departure overcommitted like a force-booked manual booking. */
export const offlineSettleRequestSchema = z
  .object({
    actor: internalBookingActorSchema,
    confirmUnpaid: z.boolean().optional(),
    overbook: z.boolean().optional(),
  })
  .strict();

/** `confirmed` is false when the booking is not paid in full yet, or when the
 *  tour date had no spots left (`noSpotsLeft`) — the payment is kept either way. */
export const offlineSettleResultSchema = z.object({
  confirmed: z.boolean(),
  noSpotsLeft: z.boolean(),
  owedMinor: z.number().int().nonnegative(),
});
export type OfflineSettleResultDto = z.infer<typeof offlineSettleResultSchema>;

/** workers → traveler-api `POST internal/v1/bookings/:bookingId/payment-deadline`.
 *  traveler-api re-reads the booking and the agency policy, then cancels or
 *  reminds once; a booking already paid, cancelled or handled is `skipped`.
 *  A Bank Transfer booking gives its held spots back on both outcomes, and is
 *  `skipped` (not marked handled) while its transfer report awaits staff. */
export const paymentDeadlineRequestSchema = z
  .object({ actor: internalBookingActorSchema })
  .strict();
export const paymentDeadlineResultSchema = z.object({
  outcome: z.enum(["cancelled", "reminded", "skipped"]),
});
export type PaymentDeadlineResultDto = z.infer<
  typeof paymentDeadlineResultSchema
>;

/** Operator bookings-list filter. Each value returns only open, unpaid
 *  bookings of its method:
 *  - `unpaid_pay_later` — Book Now, Pay Later bookings still owed.
 *  - `awaiting_transfer` — Bank Transfer bookings still owed with no report
 *    waiting for staff (includes those marked Not Received).
 *  - `transfer_reported` — Bank Transfer bookings whose report waits for staff. */
export const bookingPaymentFilterSchema = z.enum([
  "unpaid_pay_later",
  "awaiting_transfer",
  "transfer_reported",
]);
export type BookingPaymentFilter = z.infer<typeof bookingPaymentFilterSchema>;

export const OFFLINE_PAYMENT_ERROR_CODES = {
  methodUnavailable: "PAYMENT_METHOD_UNAVAILABLE",
  depositNotAllowed: "PAY_LATER_DEPOSIT_NOT_ALLOWED",
  unpaidLimitReached: "UNPAID_BOOKING_LIMIT_REACHED",
  noSpotsLeft: "NO_SPOTS_LEFT",
  paymentInFlight: "PAYMENT_IN_FLIGHT",
  switchRefused: "PAYMENT_METHOD_SWITCH_REFUSED",
  /** 403: a bank-details change without a recent identity check. operator-web
   *  answers it with Clerk's `useReverification` and retries. */
  reverificationRequired: "BANK_DETAILS_REVERIFICATION_REQUIRED",
  /** 409: the booking already has its one transfer report. */
  transferAlreadyReported: "TRANSFER_ALREADY_REPORTED",
  /** 409: not an open, unpaid Bank Transfer booking (or already marked Not
   *  Received), so this transfer action does not apply. */
  transferActionRefused: "TRANSFER_ACTION_REFUSED",
  /** 422: a transfer sent or received date after today in the agency's
   *  timezone. */
  transferDateInFuture: "TRANSFER_DATE_IN_FUTURE",
  transferProofInvalid: "TRANSFER_PROOF_INVALID",
} as const;
